// Admin · Invoices. One row per billed week, each adjustment nested under
// its week; a drawer for the lines, the send attempts and the actions; a
// panel to preview and freeze a period by hand. Every decision the JSX makes
// is a function in `lib/invoicing-view.ts`.

import { ResponsiveFilters } from '@/components/responsive-filters';
import { useMemo, useState } from 'react';
import { Dropdown } from '@/components/dropdowns';
import { createDataTable } from '@/components/data-table';
import { MutationErrorLine } from '@/components/error-panel';
import { Icons, MoneyCell } from '@/components/primitives';
import { SyncPill } from '@/components/sync-pill';
import {
  useGenerateInvoicePeriod,
  useInvoices,
  usePreviewInvoicePeriod,
} from '@/api/invoicing-hooks';
import type { InvoicePreviewResponse } from '@/api/types';
import { ago, fmtDay, fmtStampEt, nyToday, fmtDateTime } from '@/lib/datetime';
import {
  EMAIL_STATUS_LABEL,
  INVOICE_STATUS_FILTERS,
  STATE_LABEL,
  channelLabel,
  currentPeriod,
  emailStatusBand,
  emailDeliveryNote,
  emptyInvoicesMessage,
  formatCents,
  groupLinesByProduction,
  invoicesListQuery,
  periodLabel,
  periodOptions,
  shiftPeriod,
  stateBand,
  summarizeInvoices,
  toInvoiceRows,
  xeroStatusBand,
  xeroStatusLabel,
  lineMediaCents,
  linesFeeTotal,
  canRegeneratePeriod,
  hasBasisDrift,
  adjustmentChip,
  adjustmentListNote,
  isAdjustment,
  defaultInvoicesRange,
} from '@/lib/invoicing-view';
import type { InvoiceRow, InvoiceStatusFilter, InvoicesSearch } from '@/lib/invoicing-view';
import { InvoiceDrawer } from './admin-invoice-drawer';
import { PageActions } from '@/components/page-actions';

// Two-line cells need room for a compact badge, metadata and vertical padding.
const ROW_H = 56;
const { Table } = createDataTable([
  { key: 'period', header: 'Period', width: 300, mobile: 'title' },
  { key: 'state', header: 'Status', width: 160, mobile: 'status' },
  { key: 'total', header: 'Total', width: 130, align: 'num', mobile: 'stat' },
  { key: 'xero', header: 'Xero #', width: 120, priority: 3 },
  { key: 'xero_status', header: 'Xero status', width: 140, priority: 2, mobile: { role: 'stat', label: 'Xero' } },
  { key: 'paid', header: 'Paid', width: 170, align: 'num', mobile: 'stat' },
  { key: 'email', header: 'Email', width: 170, priority: 3 },
  { key: 'updated', header: 'Updated (ET)', width: 170, priority: 3, mobile: { role: 'stat', label: 'Updated' } },
]);

const STATUS_FILTER_LABEL: Record<InvoiceStatusFilter, string> = {
  all: 'All',
  pending_review: 'Pending review',
  approved: 'Approved',
  xero_created: 'In Xero',
  pdf_stored: 'PDF archived',
  emailed: 'Emailed',
  sent: 'Sent',
  paid: 'Paid',
  voided: 'Voided',
  skipped: 'No spend',
  failed: 'Failed',
};

const attentionStyle = (row: InvoiceRow) =>
  row.attention === 'review'
    ? {
        background: 'color-mix(in oklch, var(--warn) 8%, transparent)',
        boxShadow: 'inset 3px 0 0 var(--warn)',
      }
    : row.attention === 'failed'
      ? {
          background: 'color-mix(in oklch, var(--bad) 8%, transparent)',
          boxShadow: 'inset 3px 0 0 var(--bad)',
        }
      : undefined;

interface Flash {
  text: string;
}

export interface InvoicesViewProps {
  search: InvoicesSearch;
  onSearchChange: (patch: Partial<InvoicesSearch>, opts?: { replace?: boolean }) => void;
}

export function InvoicesView({ search, onSearchChange }: InvoicesViewProps) {
  const query = useMemo(
    () => invoicesListQuery(search),
    [search.from, search.to, search.status],
  );
  const listQuery = useInvoices(query);
  const invoices = listQuery.data?.invoices ?? [];
  const settings = listQuery.data?.settings;
  const rows = useMemo(() => toInvoiceRows(invoices), [invoices]);
  const summary = summarizeInvoices(invoices);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [flash, setFlash] = useState<Flash | null>(null);
  const periods = useMemo(() => periodOptions(nyToday(), 52), []);
  // The weeks a bare /admin/invoices shows: the phone Filters count reads a
  // range only once it differs from these.
  const defaultRange = useMemo(() => defaultInvoicesRange(nyToday()), []);

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Invoices</h1>
        </div>
        <PageActions>
          <button className="btn btn-primary" data-phone="inline" onClick={() => setGenerateOpen((open) => !open)}>
            <span>{Icons.plus}</span>
            <span className="phone-long">Generate period</span><span className="phone-short">Generate</span>
          </button>
          {/* On phones the summary line becomes the header's meta chip. */}
          <span className="report-freshness-chip phone-only" data-phone="meta">
            <svg className="report-freshness-clock" width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><circle cx="10" cy="10" r="7"/><path d="M10 6v4l3 2"/></svg>
            {summary.total} invoices · {formatCents(summary.billedCents)} billed
            {settings?.auto_approve ? ' · auto-approve on' : ''}
          </span>
        </PageActions>
      </div>

      {flash && (
        <div
          role="status"
          style={{
            margin: '12px 22px 0',
            padding: '10px 14px',
            background: 'color-mix(in oklch, var(--good) 12%, var(--bg-elev))',
            border: '1px solid color-mix(in oklch, var(--good) 40%, var(--line))',
            borderRadius: 'var(--r)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12.5,
          }}
        >
          <span style={{ color: 'var(--good)', fontSize: 14 }}>{Icons.check}</span>
          <span>{flash.text}</span>
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto', padding: '2px 8px' }}
            onClick={() => setFlash(null)}
            aria-label="Dismiss"
            title="Dismiss"
          >
            {Icons.x}
          </button>
        </div>
      )}

      {settings && !settings.email_delivery_tracking_enabled && invoices.some((i) => i.email_status === 'queued') && (
        <div role="status" className="muted" style={{ margin: '12px 22px 0', fontSize: 11.5 }}>
          Emails were accepted for sending. Automatic delivery verification is not configured in Admin → Config → Email.
        </div>
      )}
      {settings && !settings.enabled && (
        <div
          role="status"
          className="muted"
          style={{ margin: '12px 22px 0', fontSize: 11.5 }}
        >
          Invoicing is off in Admin → Config → Invoicing. Existing invoices are shown; nothing new
          is generated or sent until it is enabled.
        </div>
      )}

      <ResponsiveFilters className="filter-bar">
        <Dropdown
          label="FROM"
          data-filter-role="window"
          data-filter-default={defaultRange.from}
          value={search.from}
          options={periods}
          onChange={(from: string) => onSearchChange({ from })}
          width={190}
        />
        <Dropdown
          label="TO"
          data-filter-role="window"
          data-filter-default={defaultRange.to}
          value={search.to}
          options={periods}
          onChange={(to: string) => onSearchChange({ to })}
          width={190}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {INVOICE_STATUS_FILTERS.map((status) => (
            <button
              key={status}
              className={`chip${search.status === status ? ' active' : ''}`}
              aria-pressed={search.status === status}
              onClick={() => onSearchChange({ status }, { replace: true })}
            >
              {STATUS_FILTER_LABEL[status]}
            </button>
          ))}
        </div>
      </ResponsiveFilters>

      <div className="mono muted desktop-only" style={{ padding: '6px 22px', fontSize: 11 }}>
        {summary.total} invoices · {summary.pendingReview} pending review · {summary.failed} failed ·{' '}
        {formatCents(summary.billedCents)} billed · rows refresh every 30s
        {settings?.auto_approve ? ' · auto-approve is ON' : ''}
      </div>

      {generateOpen && (
        <GeneratePeriodPanel
          existing={rows}
          periods={periods}
          onQueued={(period) => {
            setFlash({
              text: `Generation queued for ${periodLabel(period)}. It appears as PENDING REVIEW within a minute.`,
            });
            setGenerateOpen(false);
          }}
          onClose={() => setGenerateOpen(false)}
        />
      )}

      <Table
        className="invoice-table"
        rows={rows}
        rowH={ROW_H}
        unfilteredTotal={rows.length}
        deps={[{ what: 'invoices', query: listQuery }]}
        empty={emptyInvoicesMessage(search)}
        noMatch="No invoices match these filters."
        onRowClick={(row) => onSearchChange({ invoice: row.id })}
        rowStyle={attentionStyle}
        renderRow={(row) => (
          <>
            <td>
              {isAdjustment(row) ? (
                <div
                  className="invoice-cell-stack"
                  data-adjustment={row.adjustment_seq}
                  style={row.nested ? { paddingLeft: 18, borderLeft: '2px solid var(--line)' } : undefined}
                >
                  <div title={adjustmentChip(row) ?? undefined}>
                    <span className="chip" style={{ fontSize: 10.5, padding: '1px 8px' }}>{adjustmentChip(row)}</span>
                  </div>
                  <div title={row.reference} className="muted mono" style={{ fontSize: 10.5 }}>{row.reference}</div>
                </div>
              ) : (
                <div className="invoice-cell-stack">
                  <div title={row.periodLabel} style={{ fontWeight: 600, color: 'var(--ink)' }}>{row.periodLabel}</div>
                  <div title={row.reference} className="muted mono" style={{ fontSize: 10.5 }}>{row.reference}</div>
                </div>
              )}
            </td>
            <td>
              <div className="invoice-cell-stack">
                <SyncPill band={stateBand(row.state)} label={STATE_LABEL[row.state]} />
                {adjustmentListNote(row) ? (
                  <span className="invoice-drift-note">{adjustmentListNote(row)}</span>
                ) : (
                  <>
                    {hasBasisDrift(row) && <span className="invoice-drift-note">Basis changed</span>}
                    {!hasBasisDrift(row) && row.basis_reporting_changed_at && <span className="muted" style={{ fontSize: 10.5 }}>Reporting updated</span>}
                  </>
                )}
              </div>
            </td>
            <td className="num">
              <MoneyCell value={row.total_cents / 100} dp={2} />
            </td>
            <td className="mono">{row.xero_invoice_number ?? '—'}</td>
            <td>
              <SyncPill band={xeroStatusBand(row.xero_status)} label={xeroStatusLabel(row.xero_status)} />
            </td>
            <td className="num">
              {row.xero_fully_paid_on ? (
                <span className="muted">paid {fmtDay(row.xero_fully_paid_on)}</span>
              ) : row.xero_amount_due_cents != null ? (
                <span className="mono" style={{ fontSize: 11 }}>
                  <span className="phone-long">{formatCents(row.xero_amount_paid_cents ?? 0)} / {formatCents(row.xero_amount_due_cents)}</span>
                  <span className="phone-short">{formatCents(row.xero_amount_paid_cents ?? 0)}</span>
                </span>
              ) : (
                <span className="muted">—</span>
              )}
            </td>
            <td>
              <div
                className="invoice-cell-stack"
                title={emailDeliveryNote(row.email_delivery_error_kind) || (row.email_delivery_checked_at ? `Delivery last checked ${fmtStampEt(row.email_delivery_checked_at)}` : 'Delivery has not been checked since sending.')}
              >
                <SyncPill band={emailStatusBand(row.email_status)} label={EMAIL_STATUS_LABEL[row.email_status]} />
                {row.last_emailed_at && (
                  <div className="muted mono" style={{ fontSize: 10.5 }}>{ago(row.last_emailed_at)}</div>
                )}
              </div>
            </td>
            <td className="muted mono" style={{ fontSize: 11 }}>
              <span className="phone-long">{fmtStampEt(row.updated_at)}</span>
              <span className="phone-short">{fmtDateTime(row.updated_at).replace(/,? \d{4},?( at)?/, ',')}</span>
            </td>
          </>
        )}
      />

      {search.invoice && (
        <InvoiceDrawer
          id={search.invoice}
          settings={settings}
          onClose={() => onSearchChange({ invoice: undefined }, { replace: true })}
        />
      )}
    </div>
  );
}

function GeneratePeriodPanel({
  existing,
  periods,
  onQueued,
  onClose,
}: {
  existing: readonly InvoiceRow[];
  periods: Array<{ id: string; label: string }>;
  onQueued: (period: string) => void;
  onClose: () => void;
}) {
  const [period, setPeriod] = useState(() => shiftPeriod(currentPeriod(), -1));
  const preview = usePreviewInvoicePeriod();
  const generate = useGenerateInvoicePeriod();
  // Only a finished week can be frozen; this week is still moving.
  const closed = periods.filter((option) => option.id < currentPeriod());
  // Generate period freezes the week's own invoice; an adjustment is raised by
  // the daily scan or Raise adjustment now, never from here.
  const blocker = existing.find(
    (row) => row.period === period && !isAdjustment(row) && !canRegeneratePeriod(row),
  );
  const ready: InvoicePreviewResponse | undefined =
    preview.data && preview.data.period === period ? preview.data : undefined;
  const canGenerate = !!ready && !blocker && !generate.isPending;

  return (
    <div className="panel" style={{ margin: '12px 22px 0' }} data-testid="generate-panel">
      <div className="panel-hd flex between items-center">
        <div className="field-lbl" style={{ margin: 0 }}>Generate period</div>
        <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close Generate period" title="Close">{Icons.x}</button>
      </div>
      <div className="panel-body">
        <div className="flex items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Dropdown
            label="PERIOD"
            value={period}
            options={closed}
            onChange={(next: string) => setPeriod(next)}
            width={200}
          />
          <button className="btn btn-sm" disabled={preview.isPending} onClick={() => preview.mutate(period)}>
            {preview.isPending ? 'Previewing…' : 'Preview'}
          </button>
          {blocker ? (
            <span className="muted" style={{ fontSize: 11.5 }}>
              {periodLabel(period)} already has an invoice ({STATE_LABEL[blocker.state]}).
            </span>
          ) : (
            <button
              className="btn btn-primary btn-sm"
              disabled={!canGenerate}
              onClick={() =>
                generate.mutate(period, {
                  onSuccess: () => onQueued(period),
                })
              }
            >
              {generate.isPending ? 'Queuing…' : `Generate ${periodLabel(period)}`}
            </button>
          )}
        </div>
        <MutationErrorLine error={preview.error ?? generate.error} />
        {ready && <PreviewTable preview={ready} />}
      </div>
    </div>
  );
}

function PreviewTable({ preview }: { preview: InvoicePreviewResponse }) {
  const groups = groupLinesByProduction(preview.lines);
  return (
    <div style={{ marginTop: 10 }}>
      {preview.warnings.length > 0 && (
        <ul
          role="status"
          className="mono"
          style={{
            margin: '0 0 10px',
            padding: '8px 12px 8px 28px',
            fontSize: 11,
            background: 'color-mix(in oklch, var(--warn) 10%, transparent)',
            border: '1px solid color-mix(in oklch, var(--warn) 35%, var(--line))',
            borderRadius: 4,
          }}
        >
          {preview.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Channel</th>
              <th className="num">Impressions</th>
              <th className="num">Media</th>
              <th className="num">Fee</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupRows
                key={group.productionId}
                name={group.productionName}
                leading={3}
                media={group.mediaCents}
                fee={group.feeCents}
                subtotal={group.subtotalCents}
              >
                {group.lines.map((line) => (
                  <tr key={line.campaign_id}>
                    <td style={{ paddingLeft: 20 }}>{line.campaign_name}</td>
                    <td className="muted">{channelLabel(line.channel)}</td>
                    <td className="num mono">{line.impressions.toLocaleString()}</td>
                    <td className="num">
                      <MoneyCell value={lineMediaCents(line) / 100} dp={2} />
                    </td>
                    <td className="num">
                      <MoneyCell value={line.fee_cents / 100} dp={2} />
                    </td>
                    <td className="num">
                      <MoneyCell value={line.spend_cents / 100} dp={2} />
                    </td>
                  </tr>
                ))}
              </GroupRows>
            ))}
            <tr>
              <td colSpan={3} style={{ fontWeight: 700 }}>Total</td>
              <td className="num" style={{ fontWeight: 700 }}>
                <MoneyCell value={(preview.total_cents - linesFeeTotal(preview.lines)) / 100} dp={2} />
              </td>
              <td className="num" style={{ fontWeight: 700 }}>
                <MoneyCell value={linesFeeTotal(preview.lines) / 100} dp={2} />
              </td>
              <td className="num" style={{ fontWeight: 700 }}>
                <MoneyCell value={preview.total_cents / 100} dp={2} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A production's header row: the name across the leading columns, then
 * its media, fee and gross subtotals under the three money columns. */
export function GroupRows({
  name,
  leading,
  media,
  fee,
  subtotal,
  children,
}: {
  name: string;
  /** How many non-money columns precede Media · Fee · Total. */
  leading: number;
  media: number;
  fee: number;
  subtotal: number;
  children: React.ReactNode;
}) {
  return (
    <>
      <tr>
        <td colSpan={leading} style={{ fontWeight: 700, paddingTop: 10 }}>{name}</td>
        <td className="num muted">
          <MoneyCell value={media / 100} dp={2} />
        </td>
        <td className="num muted">
          <MoneyCell value={fee / 100} dp={2} />
        </td>
        <td className="num muted">
          <MoneyCell value={subtotal / 100} dp={2} />
        </td>
      </tr>
      {children}
    </>
  );
}
